import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/kit";
import SquarePayment from "@/components/SquarePayment";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// Shared give form. Not tied to being signed in — the parent supplies `prefill`
// and an `onDonate(payload)` that performs the API call. Works in three modes:
// real Square card, simulated (Square not connected), or disabled preview.
export default function GiveForm({ campaign, prefill = {}, onDonate, preview = false }) {
  const suggested = (campaign.suggested_amounts && campaign.suggested_amounts.length)
    ? campaign.suggested_amounts : [2500, 5000, 10000, 25000];
  const [amount, setAmount] = useState(suggested[1] || suggested[0]);
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const [name, setName] = useState(prefill.name || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [note, setNote] = useState("");
  const [anon, setAnon] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    if (preview) return;
    api.get("/square/public-config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, [preview]);

  const amountCents = custom ? Math.round(parseFloat(customVal || "0") * 100) : amount;
  const label = campaign.give_label || campaign.title;

  const submit = async (token, orderRef) => {
    if (!amountCents || amountCents <= 0) { toast.error("Choose an amount."); return; }
    setBusy(true);
    try {
      await onDonate({
        campaign_id: campaign.campaign_id, amount_cents: amountCents,
        donor_name: name, email, note, anonymous: anon,
        source_id: token || null, order_ref: orderRef || crypto.randomUUID(),
      });
    } catch (e) {
      if (e?.response) toast.error(e.response.data?.detail || "Could not complete your gift");
    } finally { setBusy(false); }
  };

  const testBanner = cfg?.enabled && cfg?.environment === "sandbox";

  return (
    <div data-testid="give-form" className="space-y-4">
      {testBanner && (
        <div data-testid="give-test-banner" className="bg-gold/25 border border-gold/50 rounded-lg px-3 py-2 text-[13px] font-semibold text-ink">
          Test mode — Square Sandbox. No real charge is made.
        </div>
      )}
      <div>
        <div className="text-[13px] font-semibold text-ink/60 mb-1.5">Choose an amount</div>
        <div className="flex flex-wrap gap-2">
          {suggested.map((a) => (
            <button key={a} data-testid={`give-amt-${a}`} disabled={preview}
              onClick={() => { setCustom(false); setAmount(a); }}
              className={`px-4 py-2 rounded-lg text-[15px] font-semibold border transition-colors ${!custom && amount === a ? "bg-maroon text-cream border-maroon" : "border-ink/20 hover:border-maroon/40"}`}>
              {money(a)}
            </button>
          ))}
          <button data-testid="give-amt-other" disabled={preview} onClick={() => setCustom(true)}
            className={`px-4 py-2 rounded-lg text-[15px] font-semibold border transition-colors ${custom ? "bg-maroon text-cream border-maroon" : "border-ink/20 hover:border-maroon/40"}`}>
            Other
          </button>
        </div>
        {custom && (
          <input data-testid="give-custom" type="number" disabled={preview} value={customVal}
            onChange={(e) => setCustomVal(e.target.value)} placeholder="Amount ($)"
            className="mt-2 w-40 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]" />
        )}
      </div>

      <div className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[13px]" data-testid="give-designation">
        <span className="text-ink/55">Where should it go?</span> <span className="font-semibold">{label}</span>
        {campaign.is_default && <span className="text-ink/55"> — wherever Rosemoor needs it most</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[12px] font-semibold text-ink/60 block mb-1">Your name</label>
          <input data-testid="give-name" disabled={preview} value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]" />
        </div>
        <div>
          <label className="text-[12px] font-semibold text-ink/60 block mb-1">Email</label>
          <input data-testid="give-email" disabled={preview} value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]" />
        </div>
      </div>
      <div>
        <label className="text-[12px] font-semibold text-ink/60 block mb-1">Note / in honor of (optional)</label>
        <input data-testid="give-note" disabled={preview} value={note} onChange={(e) => setNote(e.target.value)}
          className="w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[15px]" />
      </div>
      <label className="flex items-center gap-2 text-[14px]">
        <input type="checkbox" data-testid="give-anon" disabled={preview} checked={anon} onChange={(e) => setAnon(e.target.checked)} />
        Give anonymously
      </label>

      {preview ? (
        <Button data-testid="give-preview-btn" disabled className="w-full opacity-70">Give {money(amountCents)} · Preview</Button>
      ) : cfg === null ? (
        <div className="text-ink/50 text-[14px]">Loading secure payment…</div>
      ) : cfg.enabled ? (
        <SquarePayment amountCents={amountCents} onToken={submit} busy={busy} />
      ) : (
        <div>
          <p className="text-[13px] text-ink/55 mb-2">Square isn't connected yet — this records a simulated gift (not a real charge, never counted as paid).</p>
          <Button data-testid="give-simulated-btn" onClick={() => submit(null, crypto.randomUUID())} disabled={busy} className="w-full">
            {busy ? "Processing…" : `Give ${money(amountCents)} (simulated)`}
          </Button>
        </div>
      )}
    </div>
  );
}
